/**
 * Labeled controlled <select> dropdown with a placeholder option and an optional
 * hover tooltip (InfoIcon) whose message is injected as HTML. Props: id, label,
 * placeholder, choices (rendered as options keyed by value), choice/setChoice
 * controlled pair, optional className and tooltipMessage.
 * Used by the bounty create/edit form (BountyCreateEdit) for category/sex/type selection.
 */
import React, { useState } from "react";
import { InfoIcon } from "lucide-react";

export type DropdownProps = {
    id: string;
    label: string;
    placeholder: string;
    choices: readonly any[];
    choice: any | null;
    setChoice: React.Dispatch<React.SetStateAction<any | null>>;
    className?: string;
    tooltipMessage?: string;
};

const Dropdown: React.FC<DropdownProps> = ({
    id,
    label,
    placeholder,
    choices,
    choice,
    setChoice,
    className,
    tooltipMessage,
}) => {
    const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

    return (
        <div className={`flex flex-col font-porsui text-slate-950 ${className}`}>
            <label htmlFor={id} className="block text-sm font-medium mb-1 tracking-widest">
                {label}
            </label>
            <div className="relative flex flex-row w-full items-center">
                <select
                    id={id}
                    value={choice || ""}
                    onChange={(e) => setChoice(e.target.value)}
                    className="w-full h-10 px-2 py-2 border rounded-lg focus:border-black focus:outline-none tracking-wider text-sm"
                >
                    <option value="" disabled>
                        {placeholder}
                    </option>
                    {choices &&
                        choices.map((choice) => (
                            <option key={choice} value={choice}>
                                {choice}
                            </option>
                        ))}
                </select>
                {tooltipMessage && (
                    <div className="relative ml-2">
                        <InfoIcon
                            className="w-6 h-6 cursor-pointer text-slate-950"
                            onMouseEnter={() => setTooltipVisible(true)}
                            onMouseLeave={() => setTooltipVisible(false)}
                        />
                        {tooltipVisible && (
                            <div
                                className="absolute right-0 md:left-0 bottom-full mb-2 w-64 p-2 bg-slate-950 text-white text-sm rounded shadow-lg z-50 whitespace-pre-line"
                                dangerouslySetInnerHTML={{ __html: tooltipMessage }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dropdown;
